import React, { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '../../utils/cn';
import { ChevronDown, Search, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectProps {
  label?: string;
  required?: boolean;
  options: MultiSelectOption[];
  error?: string;
  className?: string;
  placeholder?: string;
  value: string[]; // Selected values
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  className,
  label,
  required = false,
  options = [],
  error,
  placeholder = 'Select options...',
  value = [],
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedLabels = useMemo(() => {
    return options
      .filter((opt) => value.includes(opt.value))
      .map((opt) => opt.label);
  }, [options, value]);

  const displayText = useMemo(() => {
    if (selectedLabels.length === 0) return placeholder;
    if (selectedLabels.length <= 2) return selectedLabels.join(', ');
    return `${selectedLabels.length} Selected`;
  }, [selectedLabels, placeholder]);

  const handleToggleOption = (optionValue: string) => {
    if (disabled) return;
    const newValue = value.includes(optionValue)
      ? value.filter((val) => val !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn("flex flex-col relative w-full", className)} ref={containerRef}>
      {label && (
        <label className="block text-sm lg:text-base font-semibold text-text mb-2">
          {label}
          {required && <span className="text-[#F03434] ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={cn(
            'flex py-2.5 w-full items-center justify-between rounded-lg border border-muted bg-foreground px-4 text-base text-text ring-offset-background transition-all',
            'hover:border-teal/50 focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal',
            isOpen && 'border-teal ring-2 ring-teal/20',
            error && 'border-red-500',
            disabled && 'bg-gray-100 text-gray-500 cursor-not-allowed',
            'cursor-pointer'
          )}
        >
          <div className="flex items-center gap-3 truncate">
            <span className={cn("truncate", value.length === 0 && "text-text3")}>
              {displayText}
            </span>
          </div>
          <ChevronDown
            className={cn(
              'h-5 w-5 text-gray-400 transition-transform duration-200 ml-2',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="absolute z-50 w-full overflow-hidden rounded-lg border border-muted bg-white shadow-xl shadow-teal/5 mt-2"
            >
              <div className="max-h-60 overflow-y-auto p-1 scrollbar-thin scrollbar-thumb-teal/20 scrollbar-track-transparent">
                {options.length > 0 ? (
                  options.map((option) => {
                    const isSelected = value.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleToggleOption(option.value)}
                        className={cn(
                          "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                          isSelected 
                            ? "bg-teal/10 text-teal font-semibold" 
                            : "text-text hover:bg-teal/5"
                        )}
                      >
                        <span className="truncate">{option.label}</span>
                        {isSelected && <Check className="h-4 w-4 text-teal shrink-0 ml-2" />}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-4 text-center text-sm text-gray-500">
                    No results found
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};
